/**
 * ReviewQueue.tsx — the Reviewer's queue of Pending tools (UP-F25), oldest first, each with the
 * four-limb guardrail checklist (UP-F57) and an approve/reject decision (UP-F58/F59). Reskinned to
 * the teal "upload" design system and mounted as an in-place modal panel over AdminHome
 * (ReviewQueuePanel), rather than the old full-page navy screen.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a decision, calls reload() to refresh in place instead of a router revalidation. The still-registered
 * /review route renders through a thin ReviewQueue() wrapper that opens the same panel over a blank
 * backdrop for the deep-link path.
 *
 * SECURITY: the backend re-checks the caller's live Reviewer/PlatformOwner appointment on every call
 * (never trusted from anything cached in the browser); a caller without it gets a plain 403, shown here
 * as <NotPermitted/> rather than a crash.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, apiPost, type ReviewQueueItem } from './api';
import { shortWhen } from './shared';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  QuietAction,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type ReviewQueueLoaderData =
  { state: 'ok'; tools: ReviewQueueItem[] } | { state: 'signedOut' } | { state: 'forbidden' };

export async function reviewQueueLoader(): Promise<ReviewQueueLoaderData> {
  const res = await apiGet<{ tools: ReviewQueueItem[] }>('/api/review/queue');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/review/queue failed (${res.status})`);
  }
  return { state: 'ok', tools: res.body.tools };
}

/** The four-limb checklist plus the approve/reject decision for one Pending tool. */
function DecisionForm({ toolId, onDecided }: { toolId: string; onDecided: () => void }) {
  const [limbs, setLimbs] = useState({
    licensing: false,
    dataSafety: false,
    continuity: false,
    version: false,
  });
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allLimbsMet = limbs.licensing && limbs.dataSafety && limbs.continuity && limbs.version;

  async function decide(decision: 'approved' | 'rejected') {
    setError(null);
    if (decision === 'rejected' && notes.trim().length === 0) {
      setError('A rejection needs a reason.');
      return;
    }
    setBusy(true);
    const res = await apiPost<void>(`/api/review/${toolId}/decision`, {
      decision,
      limbLicensing: limbs.licensing,
      limbDataSafety: limbs.dataSafety,
      limbContinuity: limbs.continuity,
      limbVersion: limbs.version,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      onDecided();
      return;
    }
    if (res.kind === 'error' && res.status === 409) {
      setError('This submission has already been decided.');
      return;
    }
    // A decision is Reviewer-only (the route gates on a LIVE Reviewer appointment — PlatformOwner
    // lets you SEE the queue but not decide). Say so plainly instead of the generic failure, so a
    // permissions gap doesn't read as a random error.
    if (res.kind === 'unauthorized' || (res.kind === 'error' && res.status === 403)) {
      setError(
        'You need the Reviewer role to approve or reject. Ask a platform owner to grant it (Admin → Appointments).',
      );
      return;
    }
    setError('Could not record the decision. Please try again.');
  }

  return (
    <div>
      <SectionHeader>Guardrail checklist</SectionHeader>
      <div className="kombi-dialog-stack">
        <label className="kombi-admin-inline">
          <input
            type="checkbox"
            checked={limbs.licensing}
            onChange={(e) => setLimbs((l) => ({ ...l, licensing: e.target.checked }))}
          />
          <span className="kombi-admin-value">Licensing is in order</span>
        </label>
        <label className="kombi-admin-inline">
          <input
            type="checkbox"
            checked={limbs.dataSafety}
            onChange={(e) => setLimbs((l) => ({ ...l, dataSafety: e.target.checked }))}
          />
          <span className="kombi-admin-value">Data safety is in order</span>
        </label>
        <label className="kombi-admin-inline">
          <input
            type="checkbox"
            checked={limbs.continuity}
            onChange={(e) => setLimbs((l) => ({ ...l, continuity: e.target.checked }))}
          />
          <span className="kombi-admin-value">
            Continuity is in order (setup instructions and dependencies are genuinely adoptable)
          </span>
        </label>
        <label className="kombi-admin-inline">
          <input
            type="checkbox"
            checked={limbs.version}
            onChange={(e) => setLimbs((l) => ({ ...l, version: e.target.checked }))}
          />
          <span className="kombi-admin-value">Version and currency are in order</span>
        </label>
      </div>

      <div>
        <label className="kombi-field" htmlFor={`review-notes-${toolId}`}>
          <span>Notes (required if rejecting)</span>
          <textarea
            id={`review-notes-${toolId}`}
            maxLength={2000}
            onChange={(e) => setNotes(e.target.value)}
            value={notes}
          />
        </label>
      </div>

      {error && <ErrorLine>{error}</ErrorLine>}

      <div className="kombi-admin-row-actions">
        {/* Approve stays gated on all four guardrail limbs being met; Reject is the destructive one,
            so it is a quiet control in Kombi's error colour rather than a filled red button. */}
        <PrimaryAction disabled={busy || !allLimbsMet} onClick={() => void decide('approved')}>
          {busy ? 'Deciding…' : 'Approve'}
        </PrimaryAction>
        <QuietAction danger disabled={busy} onClick={() => void decide('rejected')}>
          Reject
        </QuietAction>
      </div>
    </div>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → reviewQueueLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the waiting-submissions list on success — each decision reload()s in place.
 */
export function ReviewQueuePanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(reviewQueueLoader);

  return (
    <KombiAdminOverlay title="Review queue" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading review queue…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the queue could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Review queue" />
      ) : data && data.state === 'ok' ? (
        <div>
          <ProvisionalNote />
          <Notice>
            Every submission waiting for a review decision, oldest first. Nothing here is decided
            automatically by the passage of time.
          </Notice>

          {data.tools.length === 0 ? (
            <EmptyState>No submissions are waiting for review.</EmptyState>
          ) : (
            <ul className="kombi-dialog-stack">
              {data.tools.map((t) => (
                <li key={t.toolId} className="kombi-admin-card">
                  <div className="kombi-admin-value">{t.name}</div>
                  <div className="kombi-admin-value kombi-admin-value--muted">
                    Submitted by {t.builderName} · waiting since {shortWhen(t.waitingSince)} · v
                    {t.version}
                  </div>
                  <dl className="kombi-dialog-stack">
                    <div>
                      <dt>Category / access / department</dt>
                      <dd className="kombi-admin-value">
                        {t.categoryLabel} · {t.accessRequirementLabel} · {t.departmentLabel} ·{' '}
                        {t.artefactType}
                      </dd>
                    </div>
                    <div>
                      <dt>Description</dt>
                      <dd className="kombi-admin-value">{t.description}</dd>
                    </div>
                    <div>
                      <dt>Problem it solves</dt>
                      <dd className="kombi-admin-value">{t.problemSolved}</dd>
                    </div>
                    <div>
                      <dt>Setup instructions</dt>
                      <dd className="kombi-admin-value">{t.setupInstructions}</dd>
                    </div>
                    <div>
                      <dt>Dependencies</dt>
                      <dd className="kombi-admin-value">{t.dependencies}</dd>
                    </div>
                    <div>
                      <dt>Estimated time saved per use</dt>
                      <dd className="kombi-admin-value">{t.estimatedTimeSavedPerUse} minutes</dd>
                    </div>
                    <div>
                      <dt>Data-safety attestation</dt>
                      <dd className="kombi-admin-value">
                        {t.attestation ? (
                          <>
                            ✓ Confirmed by {t.attestation.attesterName} on{' '}
                            {shortWhen(t.attestation.attestedAt)} · statement{' '}
                            {t.attestation.statementVersion}
                          </>
                        ) : (
                          <span className="kombi-field-error" role="alert">
                            Not attested — this submission reached the queue without a data-safety
                            attestation.
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Tags</dt>
                      <dd className="kombi-admin-value">
                        {t.tags.length > 0 ? (
                          t.tags.join(', ')
                        ) : (
                          <span className="learn-note">None</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Artefact</dt>
                      <dd className="kombi-admin-value">
                        {t.artefactRef ? (
                          <>
                            {t.artefactRef.kind === 'file'
                              ? `File: ${t.artefactRef.label}`
                              : t.artefactRef.kind === 'link'
                                ? `Link: ${t.artefactRef.label}`
                                : t.artefactRef.kind === 'prompt'
                                  ? 'Prompt'
                                  : `Blueprint: ${t.artefactRef.label}`}
                            {t.supportingCount > 0 ? ` · +${t.supportingCount} supporting` : ''}
                          </>
                        ) : (
                          <span className="learn-note">{t.artefactType}</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                  <DecisionForm toolId={t.toolId} onDecided={reload} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the still-registered /review deep link. Opens the panel always-open over a
 * blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function ReviewQueue() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <ReviewQueuePanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
